// Persistent, app-level call surface. Mounted once inside the Layout shell (above the route outlet) so an active
// meeting survives page navigation. <CallSession/> owns the call and stays mounted for the whole session: the
// floating window, the corner tile and the pop-out window are only different frames around it.
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { usePermissions } from '@/lib/permissions';
import CallSession from '@/components/meeting/call/CallSession';
import { useMeetingCall } from '@/contexts/MeetingCallContext';
import { endMeetingSession } from '@/api/morningMeeting';

const errMsg = (e, fallback) => {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg).filter(Boolean).join(', ') || fallback;
  return fallback;
};

export function ActiveCallLayer() {
  const { session, minimized, leave, minimize, maximize } = useMeetingCall();
  const { user } = useAuth();
  const { can } = usePermissions();

  if (!session) return null;

  const canManage = can('morning_meeting.manage') || user?.is_platform_admin;
  const endForAll = async () => {
    try {
      await endMeetingSession(session.session_id);
      toast.success('Meeting ended for everyone.');
    } catch (e) {
      toast.error(errMsg(e, 'Could not end the meeting.'));
    } finally { leave(); }
  };
  // Host powers (end for all, admit, lower hands) follow whoever is host right now; see useHostHandoff.
  const host = session.guest ? null : { canManage, onEndAll: endForAll };

  return (
    <CallSession key={`${session.room_url}|${session.token}`} session={session} title={session.title || 'Team Meeting'}
      mode={minimized ? 'mini' : 'window'} host={host} onMinimize={minimize} onExpand={maximize} onLeave={leave} />
  );
}

export default ActiveCallLayer;
